import { useApp } from '../../context/AppContext';
import { MiniProgress } from '../common/ProgressBar';
import { AchievementList } from '../common/AchievementPopup';
import { TIER, DIFFICULTY } from '../../data/actions';

function ActionCard({ action, isExpanded, onToggle, onCheck }) {
  const tier = TIER[action.tier];
  const difficulty = DIFFICULTY[action.difficulty];

  return (
    <div
      style={{
        background: action.done ? "rgba(34,197,94,0.06)" : "rgba(255,255,255,0.025)",
        border: `1px solid ${action.done ? "rgba(34,197,94,0.22)" : "rgba(255,255,255,0.07)"}`,
        borderRadius: "12px",
        marginBottom: "0.55rem",
        overflow: "hidden",
        transition: "border-color 0.2s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.7rem",
          padding: "0.8rem 0.9rem",
          cursor: "pointer",
        }}
        onClick={onToggle}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
      >
        {/* Checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCheck();
          }}
          aria-label={action.done ? `Unmark ${action.title}` : `Mark ${action.title} as done`}
          style={{
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            flexShrink: 0,
            border: `2px solid ${action.done ? "#4ade80" : "rgba(255,255,255,0.2)"}`,
            background: action.done ? "#4ade80" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {action.done && (
            <span style={{ color: "#080f09", fontSize: "12px", fontWeight: "bold" }}>✓</span>
          )}
        </button>

        <span style={{ fontSize: "1.2rem" }} aria-hidden="true">{action.emoji}</span>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", marginBottom: "0.1rem" }}>
            <span 
              style={{ 
                fontSize: "0.62rem", 
                color: tier.color, 
                background: tier.bg, 
                padding: "0.08rem 0.4rem", 
                borderRadius: "99px" 
              }}
            >
              {tier.label}
            </span>
            <span 
              style={{ 
                fontSize: "0.62rem", 
                color: "#4ade80", 
                background: "rgba(34,197,94,0.08)", 
                padding: "0.08rem 0.4rem", 
                borderRadius: "99px" 
              }}
            >
              ~{action.co2Saved >= 1000 ? `${(action.co2Saved/1000).toFixed(1)}t` : `${action.co2Saved}kg`} CO₂/yr
            </span>
            <span 
              style={{ 
                fontSize: "0.62rem", 
                color: difficulty.color, 
                background: difficulty.bg, 
                padding: "0.08rem 0.4rem", 
                borderRadius: "99px" 
              }}
            >
              {action.difficulty}
            </span>
          </div>
          <div 
            style={{ 
              fontSize: "0.88rem", 
              color: action.done ? "#4ade80" : "#f0fdf4", 
              textDecoration: action.done ? "line-through" : "none", 
              opacity: action.done ? 0.6 : 1 
            }}
          >
            {action.title}
          </div>
        </div>

        {/* Impact bars */}
        <div style={{ display: "flex", gap: "2px", flexShrink: 0 }} aria-label={`Impact score: ${action.impact}`}>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                width: "4px",
                height: "13px",
                borderRadius: "2px",
                background: i < Math.round(action.impact / 20) ? tier.color : "rgba(255,255,255,0.08)",
              }}
            />
          ))}
        </div>

        <span style={{ color: "#6b7280", fontSize: "0.62rem", flexShrink: 0 }} aria-hidden="true">
          {isExpanded ? "▲" : "▼"}
        </span>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div style={{ padding: "0 0.9rem 0.9rem 3.6rem", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <p style={{ color: "#9ca3af", fontSize: "0.82rem", lineHeight: 1.65, margin: "0.7rem 0 0.65rem" }}>
            {action.description}
          </p>
          <div 
            style={{ 
              background: "rgba(34,197,94,0.07)", 
              border: "1px solid rgba(34,197,94,0.15)", 
              borderRadius: "8px", 
              padding: "0.55rem 0.8rem", 
              marginBottom: "0.7rem" 
            }}
          >
            <div 
              style={{ 
                fontSize: "0.62rem", 
                color: "#4ade80", 
                textTransform: "uppercase", 
                letterSpacing: "0.1em", 
                marginBottom: "0.18rem" 
              }}
            >
              💡 First step
            </div>
            <div style={{ color: "#e8f5e9", fontSize: "0.8rem", lineHeight: 1.5 }}>
              {action.tip}
            </div>
          </div>
          <button
            onClick={onCheck}
            style={{
              background: action.done ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.13)",
              border: `1px solid ${action.done ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`,
              color: action.done ? "#fca5a5" : "#86efac",
              padding: "0.45rem 0.9rem",
              borderRadius: "8px",
              fontSize: "0.78rem",
              cursor: "pointer",
            }}
          >
            {action.done ? "↩ Unmark" : "✓ Commit to this"}
          </button>
        </div>
      )}
    </div>
  );
}

export function Actions() {
  const {
    items, toggleAction, expandedAction, setExpandedAction,
    filterTier, setFilterTier, filterDifficulty, setFilterDifficulty,
    progressPercent, doneActions, totalCO2Saved
  } = useApp();

  const filteredItems = items.filter(a => {
    if (filterTier !== "ALL" && a.tier !== filterTier) return false;
    if (filterDifficulty !== "ALL" && a.difficulty !== filterDifficulty) return false;
    return true;
  });

  return (
    <div style={{ animation: "slideIn 0.3s ease" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "0.3rem" }}>
        <h2 style={{ fontSize: "1.6rem", fontWeight: "normal", color: "#f0fdf4" }}>
          Action <em style={{ color: "#4ade80" }}>Checklist</em>
        </h2>
        <span style={{ fontSize: "0.73rem", color: "#6b7280" }}>
          {doneActions.length}/{items.length} · {totalCO2Saved > 0 ? `~${(totalCO2Saved/1000).toFixed(1)}t saved` : ""}
        </span>
      </div>
      <p style={{ color: "#6b7280", fontSize: "0.8rem", marginBottom: "1.1rem" }}>
        Click to expand. Check the circle to commit. Ranked by impact.
      </p>

      {/* Filter chips - Tier */}
      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
        {["ALL", "HIGH", "MEDIUM", "SYSTEMIC", "LOW"].map(f => (
          <button
            key={f}
            onClick={() => setFilterTier(f)}
            style={{
              padding: "0.28rem 0.65rem",
              borderRadius: "99px",
              border: "none",
              background: filterTier === f ? (TIER[f]?.color || "#4ade80") : "rgba(255,255,255,0.06)",
              color: filterTier === f ? "#fff" : "#6b7280",
              fontSize: "0.7rem",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            {f === "ALL" ? "All tiers" : TIER[f].label}
          </button>
        ))}
      </div>

      {/* Filter chips - Difficulty */}
      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", marginBottom: "1rem" }}>
        {["ALL", "Easy", "Medium", "Hard"].map(d => (
          <button
            key={d}
            onClick={() => setFilterDifficulty(d)}
            style={{
              padding: "0.25rem 0.6rem",
              borderRadius: "99px",
              border: "none",
              background: filterDifficulty === d ? (DIFFICULTY[d]?.color || "rgba(255,255,255,0.15)") : "rgba(255,255,255,0.04)",
              color: filterDifficulty === d ? "#080f09" : "#6b7280",
              fontSize: "0.68rem",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            {d === "ALL" ? "Any difficulty" : d}
          </button>
        ))}
      </div>

      {/* Mini progress */}
      <MiniProgress percent={progressPercent} />

      {/* Empty state */}
      {filteredItems.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem", color: "#4b5563", fontSize: "0.85rem" }}>
          No actions match the selected filters.
        </div>
      )}

      {/* Action list */}
      {filteredItems.map(action => (
        <ActionCard
          key={action.id}
          action={action}
          isExpanded={expandedAction === action.id}
          onToggle={() => setExpandedAction(expandedAction === action.id ? null : action.id)}
          onCheck={() => toggleAction(action.id)}
        />
      ))}

      {/* Achievements section */}
      <div style={{ marginTop: "2rem" }}>
        <div 
          style={{ 
            fontSize: "0.68rem", 
            letterSpacing: "0.15em", 
            color: "#6b7280", 
            textTransform: "uppercase", 
            marginBottom: "0.85rem" 
          }}
        >
          🏆 Achievements
        </div>
        <AchievementList />
      </div>
    </div>
  );
}
